import { getAuthHeaders } from './authService';

const BASE = `${(process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '')}/servicios-profesionales/kloud`;

async function responseFor(path, options = {}) {
  const headers = { ...getAuthHeaders() };
  const multipart = options.body instanceof FormData;
  if (multipart) delete headers['Content-Type'];
  const response = await fetch(`${BASE}/${path}`, {
    ...options, headers,
    body: options.body == null || multipart ? options.body : JSON.stringify(options.body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const failure = new Error(data.detail || data.error || `Error HTTP ${response.status}`);
    failure.status = response.status;
    throw failure;
  }
  return response;
}

export async function kloudRequest(path, options) {
  const response = await responseFor(path, options);
  const data = await response.json();
  if (data.no_config) throw new Error(data.mensaje || 'La persistencia de Kloud Deployment no esta disponible.');
  return data;
}

export async function kloudImage(path, options) {
  const response = await responseFor(path, options);
  if (!response.headers.get('content-type')?.toLowerCase().startsWith('image/png')) throw new Error('El servicio no devolvio un diagrama PNG valido.');
  return response.blob();
}

export async function kloudDocument(path, options) {
  let response;
  try { response = await responseFor(path, options); }
  catch (error) { if (error.status === 404) throw new Error('El proyecto no tiene un SOW PDF original guardado.'); throw error; }
  if (!response.headers.get('content-type')?.toLowerCase().startsWith('application/pdf')) throw new Error('El servicio no devolvio un SOW PDF valido.');
  return response.blob();
}

export async function kloudStream(path, options, onEvent) {
  const { allowFailureResult = false, ...requestOptions } = options || {};
  const response = await responseFor(path, { ...requestOptions, method: 'POST' });
  if (!response.headers.get('content-type')?.includes('text/event-stream') || !response.body) {
    throw new Error('Kloud Deployment no devolvio un flujo de eventos valido.');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let pending = '';
  let result;
  let completed = false;
  let analysisReceived = false;
  const processFrame = frame => {
    const content = frame.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n');
    if (!content) return;
    const event = JSON.parse(content);
    if (event.type === 'error') throw new Error(event.msg || event.error || 'La operacion fallo.');
    if (event.type === 'result') { result = event.data; completed = true; }
    if (event.type === 'analisis') { result = { ...event.data }; analysisReceived = true; }
    if (event.type === 'diagrama' && analysisReceived) result = { ...result, diagrama: event.data };
    if (event.type === 'done' && analysisReceived) completed = true;
    onEvent?.(event);
  };
  try {
    while (true) {
      const { done, value } = await reader.read();
      pending += decoder.decode(value, { stream: !done }).replace(/\r\n/g, '\n');
      let boundary;
      while ((boundary = pending.indexOf('\n\n')) !== -1) {
        processFrame(pending.slice(0, boundary));
        pending = pending.slice(boundary + 2);
      }
      if (done) break;
    }
    if (pending.trim()) processFrame(pending);
    if (!completed) throw new Error('La conexion termino sin resultado. Revisa el estado antes de reintentar.');
    if (!allowFailureResult && (result?.ok === false || result?.error)) throw new Error(result.error || result.summary || 'La operacion no se completo correctamente.');
    return result;
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}